// ==============================
// Imports
// ==============================

// MUI Components
import AppBarSpacer from '../components/common/AppBarSpacer';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { alpha } from '@mui/material/styles';
import { ViewList, CalendarViewWeek, CalendarMonth } from '@mui/icons-material';

// Libraries
import { useEffect, useMemo, useState } from 'react';
import {
  CalendarPlus,
  ClipboardList,
  PlusCircle,
  UserPlus,
  Users,
} from 'lucide-react';

// Local components
import MiniDrawer from '../components/MiniDrawer';
import PageHeader from '../components/common/PageHeader';
import ClassTable from '../components/ClassTable';
import { apiGet } from '../utils/apiClient';
import {
  ClassOverviewThisWeek,
  ClassOverviewThisWeekResponse,
} from '../types/ClassOverview.type';
import { notifyFailure } from '../utils/toast';
import { useTranslation } from '../i18n';

const ClassLayout = () => {
  const { t } = useTranslation();
  // States
  const [open, setOpen] = useState(true);

  const [overview, setOverview] = useState<ClassOverviewThisWeek | null>(null);
  const [isLoadingOverview, setIsLoadingOverview] = useState(false);

  // Dialog state for the add-class buttons (rendered above the metrics); the
  // dialogs themselves live inside <ClassTable/>, driven via these props.
  const [openCreateClassDialog, setOpenCreateClassDialog] = useState(false);
  const [openQuickAddClassDialog, setOpenQuickAddClassDialog] = useState(false);
  const [calendarView, setCalendarView] = useState(false);
  const [viewMode, setViewMode] = useState<'all' | 'grouped'>('all');

  useEffect(() => {
    let isMounted = true;

    const fetchOverview = async () => {
      try {
        setIsLoadingOverview(true);
        const res = await apiGet<ClassOverviewThisWeekResponse>(
          '/class/overview/this-week',
        );
        if (!isMounted) return;
        setOverview(res.data);
      } catch {
        notifyFailure();
      } finally {
        if (isMounted) setIsLoadingOverview(false);
      }
    };

    fetchOverview();
    return () => {
      isMounted = false;
    };
  }, []);

  const statCards = useMemo(
    () => [
      {
        title: t('class.metricNewClasses'),
        value:
          isLoadingOverview || !overview ? '—' : overview.newClassesThisWeek,
        icon: <CalendarPlus size={20} />,
        tone: 'primary' as const,
      },
      {
        title: t('class.metricNewStudents'),
        value:
          isLoadingOverview || !overview ? '—' : overview.newStudentsThisWeek,
        icon: <UserPlus size={20} />,
        tone: 'success' as const,
      },
      {
        title: t('class.metricNewEnrollments'),
        value:
          isLoadingOverview || !overview
            ? '—'
            : overview.newEnrollmentsThisWeek,
        icon: <ClipboardList size={20} />,
        tone: 'warning' as const,
      },
      {
        title: t('class.metricTotalStudents'),
        value:
          isLoadingOverview || !overview ? '—' : overview.totalStudentsThisWeek,
        icon: <Users size={20} />,
        tone: 'info' as const,
      },
    ],
    [isLoadingOverview, overview, t],
  );

  // ==============================
  // Render
  // ==============================
  return (
    <>
      <div className="flex h-screen">
        {/* Sidebar */}
        <MiniDrawer open={open} setOpen={setOpen} />

        {/* Main content */}
        <main className="flex-1 overflow-auto app-page-bg">
          {/* Offset for fixed AppBar (height = 64px) */}
          <AppBarSpacer />

          <div className="h-full px-6 py-4">
            <PageHeader
              title={t('class.pageTitle')}
              subtitle={t('class.pageSubtitle')}
            />

            {/* One row: view tabs on the left, add-class buttons on the right */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                mb: 2,
              }}
            >
              <ToggleButtonGroup
                value={calendarView ? 'calendar' : viewMode}
                exclusive
                onChange={(
                  _,
                  newValue: 'all' | 'grouped' | 'calendar' | null,
                ) => {
                  if (!newValue) return;
                  if (newValue === 'calendar') {
                    setCalendarView(true);
                    return;
                  }
                  setCalendarView(false);
                  setViewMode(newValue);
                }}
                size="small"
                aria-label="class management view mode"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  '& .MuiToggleButton-root': { flex: { xs: 1, sm: 'none' } },
                }}
              >
                <ToggleButton
                  value="all"
                  aria-label="show all"
                  sx={{ p: { xs: 1, sm: 1.5 }, fontSize: { xs: '0.72rem', sm: '0.8125rem' } }}
                >
                  <ViewList sx={{ mr: 0.5 }} />
                  {t('class.viewShowAll')}
                </ToggleButton>
                <ToggleButton
                  value="grouped"
                  aria-label="group by week"
                  sx={{ p: { xs: 1, sm: 1.5 }, fontSize: { xs: '0.72rem', sm: '0.8125rem' } }}
                >
                  <CalendarViewWeek sx={{ mr: 0.5 }} />
                  {t('class.viewGroupByWeek')}
                </ToggleButton>
                <ToggleButton
                  value="calendar"
                  aria-label="view calendar"
                  sx={{ p: { xs: 1, sm: 1.5 }, fontSize: { xs: '0.72rem', sm: '0.8125rem' } }}
                >
                  <CalendarMonth sx={{ mr: 0.5 }} />
                  {t('class.viewCalendar')}
                </ToggleButton>
              </ToggleButtonGroup>

              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenCreateClassDialog(true)}
                  startIcon={<PlusCircle size={18} />}
                  sx={{ color: 'white' }}
                >
                  {t('class.addSingleClass')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenQuickAddClassDialog(true)}
                  startIcon={<PlusCircle size={18} />}
                  sx={{ color: 'white' }}
                >
                  {t('class.addClasses')}
                </Button>
              </Box>
            </Box>

            {/* This-week overview */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {statCards.map((stat) => (
                <Grid key={stat.title} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                    sx={(theme) => ({
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      boxShadow: theme.shadows[2],
                    })}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          fontWeight="medium"
                        >
                          {stat.title}
                        </Typography>

                        <Box
                          sx={(theme) => {
                            const main = theme.palette[stat.tone].main;
                            return {
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 1,
                              borderRadius: '50%',
                              backgroundColor: alpha(main, 0.12),
                              color: main,
                            };
                          }}
                        >
                          {stat.icon}
                        </Box>
                      </Box>
                      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                        {stat.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Class table */}
            <ClassTable
              openCreateClassDialog={openCreateClassDialog}
              setOpenCreateClassDialog={setOpenCreateClassDialog}
              openQuickAddClassDialog={openQuickAddClassDialog}
              setOpenQuickAddClassDialog={setOpenQuickAddClassDialog}
              calendarView={calendarView}
              onCalendarViewChange={setCalendarView}
              viewMode={viewMode}
            />
          </div>
        </main>
      </div>
    </>
  );
};

export default ClassLayout;
